import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { userQueryOptions } from "@/services/user/user.options";
import { useAuthState } from "@/stores/auth-hooks";

/**
 * Hook to get a user's avatar as an object URL safe for use in <img src>.
 *
 * Requests GET /users/{userId}/avatar only when the loaded user record's
 * hasAvatar flag is true. A false or missing flag keeps initials and does
 * not call the endpoint. When the flag is omitted, the signed-in user's
 * hasAvatar value is used for that same user so the profile drawer can stay
 * on the session record.
 *
 * @param userId - The user's UUID
 * @param hasAvatar - Whether the loaded user record has an avatar
 * @returns Object with:
 *   - src: The object URL for the avatar image, or null if not available
 *   - isLoading: Whether the avatar is being fetched
 *   - isError: Whether there was an error fetching the avatar
 *   - refetch: Function to manually refetch the avatar
 */
export function useUserAvatarSrc(
	userId: string | undefined,
	hasAvatar?: boolean,
) {
	const signedInHasAvatar = useAuthState((state) => {
		const user = state.user;
		if (hasAvatar !== undefined || !user || user.id !== userId) return undefined;
		return user.hasAvatar;
	});
	const shouldLoad = hasAvatar ?? signedInHasAvatar;
	const [objectUrl, setObjectUrl] = useState<string | null>(null);
	// Use a ref to track the current URL for cleanup without causing re-renders
	const objectUrlRef = useRef<string | null>(null);

	const {
		data: avatarBlob,
		isLoading,
		isError,
		refetch,
	} = useQuery({
		...userQueryOptions.avatar(userId ?? ""),
		enabled: !!userId && userId.length > 0 && shouldLoad === true,
	});

	// Convert blob to object URL when blob changes
	useEffect(() => {
		// Revoke previous URL if exists
		if (objectUrlRef.current) {
			URL.revokeObjectURL(objectUrlRef.current);
			objectUrlRef.current = null;
		}

		// Create new object URL if we have a blob
		if (avatarBlob) {
			const url = URL.createObjectURL(avatarBlob);
			objectUrlRef.current = url;
			setObjectUrl(url);
		} else {
			setObjectUrl(null);
		}

		// Cleanup: revoke URL when component unmounts or blob changes
		return () => {
			if (objectUrlRef.current) {
				URL.revokeObjectURL(objectUrlRef.current);
				objectUrlRef.current = null;
			}
		};
	}, [avatarBlob]);

	return {
		src: objectUrl,
		isLoading,
		isError,
		refetch,
	};
}
